import { FlagshipProject, FeaturedProject, MetricItem, SubprojectItem } from '../types';

export const flagshipProject: FlagshipProject = {
  title: 'AI-Powered Natural Language-to-SQL Agent',
  badge: 'Solo Architected POC · 2026',
  tagline: 'Governed multi-agent AI pipeline translating plain English into validated, production-safe SQL across 400+ enterprise tables.',
  summary: 'Architected and built a solo multi-agent AI system that empowers support engineers and business stakeholders to safely query enterprise databases without writing SQL, protected by AST validators, semantic vector search, Keycloak RBAC, and human approval gates.',
  problem: 'Enterprise databases with 400+ tables exceed LLM context windows and risk hallucinated joins or destructive writes. Direct database access for support teams creates security vulnerabilities and operational bottlenecks.',
  solution: 'A 5-stage governed pipeline using LangGraph state machines, pgvector semantic DDL retrieval, AST-level SELECT-only enforcement, Keycloak RBAC table/row-level filters, and human-in-the-loop signoff for sensitive schemas.',
  stack: [
    'LangGraph',
    'LangChain',
    'OpenAI LLM APIs',
    'pgvector',
    'FastAPI',
    'Python',
    'PostgreSQL',
    'Keycloak RBAC',
    'Google Chat Webhooks',
    'Google Drive API'
  ],
  pipelineStages: [
    {
      id: 'intent-routing',
      step: '01',
      title: 'Intent Classification & Routing',
      tech: 'FastAPI · OpenAI LLM APIs',
      shortDesc: 'Distinguishes analytical SQL requests from general inquiries.',
      fullDesc: 'Classifies every incoming natural language request at the FastAPI edge gateway. General inquiries are answered directly, while analytical questions are normalized and routed into the SQL execution graph.',
      highlights: [
        'Zero LLM compute wasted on non-database prompts',
        'Request normalization and parameter extraction',
        'Deterministic state graph initialization'
      ]
    },
    {
      id: 'schema-rag',
      step: '02',
      title: 'Semantic Schema Retrieval',
      tech: 'pgvector · PostgreSQL RAG · 400+ Tables',
      shortDesc: 'Embeds table DDLs to retrieve exact schemas at runtime.',
      fullDesc: 'Overcomes LLM token window limits across an enterprise schema of 400+ tables. Table DDLs and column descriptions are embedded with pgvector so only top-k relevant table schemas are dynamically injected into the prompt context.',
      highlights: [
        'Eliminates context overflow across 400+ database tables',
        'Dynamic semantic search matches natural terms to actual columns',
        'Zero code changes required when database schema evolves'
      ]
    },
    {
      id: 'agent-guardrails',
      step: '03',
      title: 'Multi-Agent Generation & Guardrails',
      tech: 'LangGraph State Machines · AST Parser',
      shortDesc: 'Generates SQL with strict SELECT-only AST validation.',
      fullDesc: 'A multi-agent state graph where a schema-aware generator agent drafts the query, and an independent validator agent parses the SQL Abstract Syntax Tree (AST) to strictly enforce read-only/SELECT-only guardrails, query timeouts, and row limits.',
      highlights: [
        'Strict AST validation guarantees 0 write/mutation operations (INSERT/UPDATE/DELETE/DROP)',
        'Automatic injection of LIMIT clauses and execution timeouts',
        'Query-repair fallback loop prompts user for clarification on unmappable terms'
      ]
    },
    {
      id: 'governance-hitl',
      step: '04',
      title: 'Governance & HITL Review Gates',
      tech: 'Keycloak RBAC · Human-in-the-Loop',
      shortDesc: 'Enforces role permissions and approval for sensitive data.',
      fullDesc: 'Applies Keycloak-driven Role-Based Access Control (RBAC) across sales, product, checkout, and customer domains. Any query touching sensitive customer or payment data is automatically suspended for Human-in-the-Loop (HITL) approval.',
      highlights: [
        'Row and table-level access governance mapped to Keycloak user roles',
        'Automated quarantine and human approval gate for sensitive/PII data',
        'Comprehensive audit logging of all generated queries and reviewer decisions'
      ]
    },
    {
      id: 'delivery-context',
      step: '05',
      title: 'Context Management & Async Delivery',
      tech: 'Session Memory · Google Drive · Google Chat',
      shortDesc: 'Handles follow-ups and offloads large exports asynchronously.',
      fullDesc: 'Maintains multi-turn conversation memory for analytical drill-downs. When result sets exceed 100 rows, the engine offloads CSV generation to Google Drive and pushes an asynchronous completion webhook alert to Google Chat.',
      highlights: [
        'Multi-turn context retention allows conversational query refinements',
        'Automated background export to Google Drive for large result sets (>100 rows)',
        'Google Chat async webhook notifications keep engineers in their workflow'
      ]
    }
  ],
  guardrails: [
    {
      label: 'SELECT-Only AST Validator',
      desc: 'Zero write/mutation commands (INSERT, UPDATE, DELETE, DROP, ALTER) allowed into execution.'
    },
    {
      label: 'Keycloak RBAC & HITL Gate',
      desc: 'Role-based access per table domain; queries touching sensitive payment or customer data require human approval.'
    },
    {
      label: 'Automated Async Offloading',
      desc: 'Query results over 100 rows trigger Google Drive CSV generation and Google Chat webhook notifications.'
    }
  ]
} as const;

export const featuredProjects: readonly FeaturedProject[] = [
  {
    id: 'langgraph-on-the-job',
    title: 'LangGraph on the Job: Enterprise Agent Framework',
    badge: 'Autonomous Multi-Agent Architecture · 2026',
    tagline: 'Practical, production-pattern repository demonstrating multi-agent workflows, state persistence, cyclic graphs, and custom tool-calling guardrails.',
    desc: 'Production-grade repository implementing an autonomous, cyclic multi-agent state machine built on LangGraph, ChromaDB, and OpenAI LLM APIs. Demonstrates practical enterprise patterns including state persistence, custom tool-calling middleware, dynamic schema grounding, and human-in-the-loop intervention loops.',
    highlights: [
      'Cyclic LangGraph StateGraph workflow executing: Plan ──► Ingest ──► Retrieve ──► Synthesize ──► Verify ──► Audit',
      'Sentence-level claim decomposition with strict 0.70 grounding score verification threshold and canary leak defense',
      'Persistent ChromaDB vector store with text-embedding-3-small, L2 normalization, and fallback to dense TF-IDF vectors',
      'Dual-mode execution (live OpenAI models or offline zero-dependency heuristic reasoning) with 95.5%+ test coverage'
    ],
    stack: ['Python', 'LangGraph', 'LangChain', 'ChromaDB', 'OpenAI APIs', 'Pydantic', 'Asyncio', 'Pytest'],
    githubUrl: 'https://github.com/nagendradixit647/langgraph-on-the-job',
    architectureNotes: 'Orchestrator ➔ Memory / Retriever ➔ Analyst (Cross-Doc Reasoning) ➔ Verifier (Claim Verification) ➔ Guardrail Output'
  },
  {
    id: 'portfolio-cicd-engine',
    title: 'Personal Developer Portfolio & CI/CD Delivery Engine',
    badge: 'React 19 + TypeScript Platform · 2026',
    tagline: 'Minimalist, type-safe developer portfolio engineered with React 19, TypeScript, and automated GitHub Actions CI/CD.',
    desc: 'Bespoke, high-performance portfolio application following content-first minimal aesthetics inspired by abbasraza.dev. Features strict PII privacy controls, pure Vanilla CSS tokens, interactive SVG pipeline inspection, and automated GitHub Actions CI/CD with rollback dispatch and Netlify deployment.',
    highlights: [
      'Zero-PII privacy design with interactive Resume Modal and direct LinkedIn integration',
      'Strict TypeScript schema typing across all project, experience, skill, and pipeline entities',
      'Zero third-party CSS overhead with fluid responsive grid and subtle dark-mode glowing accents',
      'Automated GitHub Actions CI/CD pipeline featuring manual rollback dispatch triggers and tag builds'
    ],
    stack: ['React 19', 'TypeScript', 'Vanilla CSS3', 'GitHub Actions', 'Netlify'],
    githubUrl: 'https://github.com/nagendradixit647/personal-portfolio',
    liveUrl: 'https://nvdixit.co.uk'
  },
  {
    id: 'flip-clock',
    title: 'Flip Clock: Retro Split-Flap Clock & Native Screensaver',
    badge: 'Cross-Platform Desktop & Android · 2026',
    tagline: 'High-performance vintage split-flap clock engine with authentic 3D mechanical flip physics, multi-display synchronization, WiX v4 MSI installer, and ultra-low battery Android Live Wallpaper.',
    desc: 'Cross-platform mechanical split-flap clock engineered with Python/PyQt6 for desktop and Kotlin/Android SDK for mobile. Replaces heavy web-wrapped Electron utilities with an ultra-lightweight, native architecture featuring cosine-interpolated 3D flip mechanics, multi-monitor display synchronization with per-screen timezones, Win32 sleep-inhibition hooks, an enterprise WiX Toolset v4 installer, and an Android Live Wallpaper running at <0.1% daily battery drain.',
    highlights: [
      'Authentic 3D Split-Flap Animation: Hardware-accelerated vector canvas rendering with smooth cosine-interpolated card flip physics',
      'Multi-Monitor Synchronization & Per-Display Timezones: Dynamic display discovery with independent timezone assignment (UTC, London, NY, Tokyo) and QSettings persistence',
      'Win32 Power Management: Direct kernel32.dll SetThreadExecutionState integration preventing sleep while active, restoring power states on exit',
      'Enterprise Packaging & Code Signing: WiX Toolset v4 MSI installer, native Windows screensaver (.scr), Authenticode signing pipeline, and Android SDK WallpaperService APK (<0.1% battery/day)'
    ],
    stack: ['Python 3.12', 'PyQt6', 'Kotlin', 'Android SDK', 'WiX v4', 'PyInstaller', 'Pydantic v2', 'Win32 API', 'Pytest'],
    githubUrl: 'https://github.com/nagendradixit647/flip-clock',
    liveUrl: 'https://github.com/nagendradixit647/flip-clock/releases',
    architectureNotes: 'PyQt6 3D Physics Engine ➔ Win32 Kernel32 Hook ➔ Multi-Display Sync ➔ WiX Toolset v4 MSI & Android WallpaperService'
  }
] as const;

export const metrics: readonly MetricItem[] = [
  {
    num: '18–22',
    unit: 'hrs / mo',
    title: 'Automated Surcharge Performance Analytics Engine',
    tag: 'Enterprise Reporting Automation · 2025',
    desc: 'Replaced a manual multi-step reporting process (~10–30 min daily database extractions, manual spreadsheets, and pivot tables) with a scheduled Python engine via GitLab CI/CD runners and a companion Google Apps Script system for automatic spreadsheet consolidation, pivot refreshes, and Google Chat alerts.',
    stack: ['Python', 'GitLab CI/CD', 'Google Apps Script', 'Google Drive API', 'Google Chat'],
    impact: '18–22 hours saved per month (~240 hrs/yr)'
  },
  {
    num: '167.3',
    unit: 'hrs / yr',
    title: 'Secure Financial Reporting Delivery Pipeline',
    tag: 'Data Pipeline & Encryption · 2025',
    desc: 'Built an automated data extraction and encrypted reporting pipeline for Barclays Smartpay transaction reconciliation. Uses GitLab CI/CD runners with masked variable encryption and Google Apps Script to package, encrypt, and deliver daily reports to client stakeholders securely.',
    stack: ['Python', 'GitLab CI/CD', 'Barclays Smartpay', 'Google Apps Script', 'AES Encryption'],
    impact: '167.3 hours saved annually (~21 working days)'
  },
  {
    num: '99.8%',
    unit: 'faster',
    title: 'Keycloak Bulk Till Role Automation Workbench',
    tag: 'Identity Provisioning & Governance · 2025',
    desc: 'Replaced a manual till-role provisioning workflow that took ~5 minutes per user/group with a high-throughput Python automation workbench featuring CSV validation, concurrent API execution, and secure credential management via Keycloak REST API.',
    stack: ['Python', 'Keycloak REST API', 'RBAC Provisioning', 'Async Concurrency', 'Security Automation'],
    impact: '79.4 hours saved annually with 99.8% execution speedup'
  }
] as const;

export const subprojects: readonly SubprojectItem[] = [
  {
    stat: 'Production Platform · 2026',
    title: 'ServiceNow Catalogue File Validation Microservice',
    tag: 'Microservices & Cloud Infrastructure',
    desc: 'Architected and built a containerized Python validation microservice deployed on Kubernetes. Integrated with ServiceNow catalogue workflows to perform deep schema and data integrity validation (mandatory fields, data types, special characters, duplicates, empty values) before staging files to Amazon S3.',
    stack: ['Kubernetes', 'Python', 'Amazon S3', 'ServiceNow', 'Docker', 'FastAPI']
  },
  {
    stat: 'Observability & Operations · 2026',
    title: 'Datadog to ServiceNow Incident Automation',
    tag: 'Observability & AIOps',
    desc: 'Engineered an intelligent observability-to-incident automation pipeline connecting Datadog monitoring with ServiceNow. Automatically triages and creates incidents from Kubernetes CrashLoopBackOff alert conditions, eliminating manual ticket creation and improving MTTR.',
    stack: ['Datadog', 'ServiceNow', 'Kubernetes', 'AIOps', 'Incident Management']
  }
] as const;
